import { useRef } from "react";
import { useImmer } from "use-immer";
import { errorMessage, printFileHelper, saveFileHelper } from "../../../utils";
import { exportFtiPdfApi } from "../apis";

const fetchFtiPdf = async ({ id, fti_invoice_number }) => ({
  response: await exportFtiPdfApi(id),
  fileName: `FTI-${fti_invoice_number ?? id}.pdf`,
});

// `action` is "download" to save the PDF or "print" to open the browser's print dialog
const deliverFtiPdf = ({ response, fileName }, action) => {
  if (action === "print") {
    printFileHelper(response.data);
  } else {
    saveFileHelper({ response, fileName });
  }
};

// Shared by the details page's new FTI and reprint flows. The PDF is either fetched once the
// choice is made (`exportFtiPdf`), or fetched first and handed over later (`generateFtiPdf`
// then `deliverGeneratedPdf`), e.g. so a reprint is recorded before asking download or print
export const useFtiPdfExport = () => {
  const [state, setState] = useImmer({
    // The choice being exported by `exportFtiPdf` ("download" | "print" | null)
    exportingAction: null,
    isGenerating: false,
  });
  // Not rendered, and immer would freeze the axios response, so it's kept in a ref
  const generatedPdf = useRef(null);

  const exportFtiPdf = async (fti, action = "download") => {
    setState((draft) => {
      draft.exportingAction = action;
    });
    try {
      deliverFtiPdf(await fetchFtiPdf(fti), action);
    } catch (error) {
      errorMessage("Failed to export the full tax invoice PDF");
    } finally {
      setState((draft) => {
        draft.exportingAction = null;
      });
    }
  };

  // Resolves to whether the PDF was fetched, so the caller only moves on when it's ready
  const generateFtiPdf = async (fti) => {
    setState((draft) => {
      draft.isGenerating = true;
    });
    try {
      generatedPdf.current = await fetchFtiPdf(fti);
      return true;
    } catch (error) {
      errorMessage("Failed to export the full tax invoice PDF");
      return false;
    } finally {
      setState((draft) => {
        draft.isGenerating = false;
      });
    }
  };

  const deliverGeneratedPdf = (action) => {
    deliverFtiPdf(generatedPdf.current, action);
    generatedPdf.current = null;
  };

  return {
    exportingAction: state.exportingAction,
    isGenerating: state.isGenerating,
    exportFtiPdf,
    generateFtiPdf,
    deliverGeneratedPdf,
  };
};
